import { Component } from '@theme/component';

/**
 * SORIVO luxury fly-to-cart animation.
 * Uses direct element geometry so it also works reliably with the custom SORIVO header.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean | string} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.remove();
      return;
    }

    requestAnimationFrame(() => {
      const sourceRect = this.source.getBoundingClientRect();
      const destinationRect = this.destination.getBoundingClientRect();

      if (!sourceRect.width || !sourceRect.height || !destinationRect.width || !destinationRect.height) {
        this.remove();
        return;
      }

      this.#animate(sourceRect, destinationRect);
    });
  }

  /**
   * @param {DOMRect} sourceRect
   * @param {DOMRect} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const start = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const end = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    const dx = end.x - start.x;
    const dy = end.y - start.y;

    if (this.useSourceSize) {
      const maxSize = 92;
      const ratio = Math.min(1, maxSize / Math.max(sourceRect.width, sourceRect.height));
      this.style.width = `${Math.max(44, sourceRect.width * ratio)}px`;
      this.style.height = `${Math.max(44, sourceRect.height * ratio)}px`;
    }

    this.style.left = `${start.x}px`;
    this.style.top = `${start.y}px`;
    this.style.opacity = '1';

    const flight = this.animate(
      [
        {
          transform: 'translate(-50%, -50%) translate3d(0, 0, 0) scale(.86) rotate(0deg)',
          opacity: 0,
          filter: 'blur(.7px) saturate(.96)',
        },
        {
          transform: 'translate(-50%, -50%) translate3d(0, -5px, 0) scale(1) rotate(0deg)',
          opacity: 1,
          filter: 'blur(0) saturate(1)',
          offset: 0.11,
        },
        {
          transform: `translate(-50%, -50%) translate3d(${dx * 0.56}px, ${dy * 0.56 - 30}px, 0) scale(.68) rotate(-2deg)`,
          opacity: 1,
          filter: 'blur(0) saturate(1.03)',
          offset: 0.58,
        },
        {
          transform: `translate(-50%, -50%) translate3d(${dx}px, ${dy}px, 0) scale(.13) rotate(2deg)`,
          opacity: 0.15,
          filter: 'blur(.3px) saturate(.96)',
        },
      ],
      {
        duration: this.classList.contains('fly-to-cart--quick') ? 700 : 800,
        easing: 'cubic-bezier(.22,.8,.2,1)',
        fill: 'forwards',
      }
    );

    try {
      await flight.finished;
    } catch (error) {
      // Ignore cancellations caused by navigation or section refreshes.
    }

    this.#arrival(destinationRect);
    this.remove();
  };

  /**
   * @param {DOMRect} destinationRect
   */
  #arrival = (destinationRect) => {
    if (!this.destination?.isConnected) return;

    this.destination.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.09)', offset: 0.38 },
        { transform: 'scale(.985)', offset: 0.72 },
        { transform: 'scale(1)' },
      ],
      {
        duration: 460,
        easing: 'cubic-bezier(.22,.8,.2,1)',
      }
    );

    const ring = document.createElement('span');
    ring.className = 'sorivo-cart-arrival-ring';
    ring.setAttribute('aria-hidden', 'true');
    ring.style.left = `${destinationRect.left + destinationRect.width / 2}px`;
    ring.style.top = `${destinationRect.top + destinationRect.height / 2}px`;
    ring.style.color = getComputedStyle(this.destination).color || '#111';
    document.body.appendChild(ring);

    ring.addEventListener('animationend', () => ring.remove(), { once: true });
    setTimeout(() => ring.remove(), 900);
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
